import { action, computed, makeObservable, observable } from 'mobx';
import {
  computeValueAtRisk,
  ValueAtRiskInput,
  valueAtRiskMissingFigures,
  ValueAtRiskResult,
  ValueAtRiskSectionInput,
  ValueAtRiskSectionKind,
} from '@clipr/lib';

type Row = ValueAtRiskSectionInput['rows'][number];

/** What the server suggests from the recording (backend value-at-risk-extractor.service.ts). */
export interface ValueAtRiskSuggestion {
  sections: Array<{
    kind: ValueAtRiskSectionKind;
    name?: string;
    sumInsured?: string;
    sumInsuredQuote?: string;
    rows: Array<Row & { quote: string }>;
  }>;
}

const ROW_FIELDS = ['length', 'width', 'storeys', 'rate', 'amount'] as const;

function isBlank(value: string | null | undefined): boolean {
  return !value?.trim();
}

function sameText(a: string | null | undefined, b: string | null | undefined): boolean {
  return (a ?? '').trim().toLocaleLowerCase() === (b ?? '').trim().toLocaleLowerCase();
}

function isEmptyRow(row: Row): boolean {
  return isBlank(row.description) && ROW_FIELDS.every(field => isBlank(row[field]));
}

type SectionField = 'name' | 'sumInsured' | 'boundaryWalls' | 'drives' | 'otherCosts';
type RowField = keyof Row;

function emptyRow(): Row {
  return { description: '' };
}

function emptySection(kind: ValueAtRiskSectionKind): ValueAtRiskSectionInput {
  return { kind, rows: [emptyRow()] };
}

/** AWSJSON arrives as a string from AppSync; tolerate an object too. */
function savedInput(saved: unknown): ValueAtRiskInput | null {
  const value: unknown = typeof saved === 'string' ? JSON.parse(saved) : saved;
  const input = (value as { input?: ValueAtRiskInput } | null)?.input;
  return input && Array.isArray(input.sections) ? input : null;
}

/**
 * The Value at Risk form. Figures shown here are a live preview from the shared calculator; the
 * server recomputes them on save and only its result reaches the report.
 */
export class ValueAtRiskState {
  constructor() {
    makeObservable<this, 'savedInputJson'>(this, {
      input: observable,
      suggestionQuotes: observable,
      savedInputJson: observable,
      preview: computed,
      isDirty: computed,
      missingFigures: computed,
      load: action,
      markSaved: action,
      applySuggestion: action,
      setThreshold: action,
      addSection: action,
      removeSection: action,
      setSectionField: action,
      setUnlimited: action,
      addRow: action,
      removeRow: action,
      setRowField: action,
    });
  }

  input: ValueAtRiskInput = { sections: [emptySection('Buildings')] };
  /** Transcript quotes behind the last pre-fill, shown so the adjuster can check them. */
  suggestionQuotes: string[] = [];
  /** The form as last loaded or saved. */
  private savedInputJson = JSON.stringify(this.input);

  /** True when the form holds changes the report doesn't have. */
  get isDirty(): boolean {
    return JSON.stringify(this.input) !== this.savedInputJson;
  }

  get preview(): { result: ValueAtRiskResult | null; error: string | null } {
    try {
      return { result: computeValueAtRisk(this.input), error: null };
    } catch (error: unknown) {
      return { result: null, error: (error as Error).message };
    }
  }

  /** Why Save is off when nothing is wrong with what was typed; the preview's error comes first. */
  get missingFigures(): string | null {
    return this.preview.error ? null : valueAtRiskMissingFigures(this.input);
  }

  load(saved: unknown) {
    this.input = savedInput(saved) ?? { sections: [emptySection('Buildings')] };
    this.savedInputJson = JSON.stringify(this.input);
    this.suggestionQuotes = [];
  }

  markSaved(inputJson: string) {
    this.savedInputJson = inputJson;
  }

  /**
   * Merges suggestions from the recording without overwriting anything the adjuster typed: a row
   * with the same description only gets its empty fields filled, an empty row is reused, and
   * anything else is added. Returns how many rows were filled or added.
   */
  applySuggestion(suggestion: ValueAtRiskSuggestion): number {
    let filled = 0;
    const quotes: string[] = [];
    // A site can have two buildings with one name, so a row takes one suggestion per pre-fill.
    const taken = new Set<Row>();
    for (const suggested of suggestion.sections) {
      let section = this.input.sections.find(existing => existing.kind === suggested.kind
        && (suggested.kind !== 'Other' || sameText(existing.name, suggested.name)));
      if (!section) {
        section = { kind: suggested.kind, ...(suggested.name && { name: suggested.name }), rows: [] };
        this.input.sections.push(section);
        section = this.input.sections[this.input.sections.length - 1];
      }
      if (suggested.sumInsured && isBlank(section.sumInsured) && !section.sumInsuredUnlimited) {
        section.sumInsured = suggested.sumInsured;
        if (suggested.sumInsuredQuote) quotes.push(suggested.sumInsuredQuote);
      }
      for (const { quote, ...values } of suggested.rows) {
        const target = section.rows.find(row => !taken.has(row) && sameText(row.description, values.description))
          ?? section.rows.find(isEmptyRow);
        if (target) {
          if (isBlank(target.description)) target.description = values.description;
          for (const field of ROW_FIELDS) {
            if (isBlank(target[field]) && !isBlank(values[field])) target[field] = values[field];
          }
          taken.add(target);
        } else {
          section.rows.push(values);
          taken.add(section.rows[section.rows.length - 1]);
        }
        quotes.push(quote);
        filled += 1;
      }
    }
    this.suggestionQuotes = quotes;
    return filled;
  }

  setThreshold(value: string) {
    this.input.thresholdPercent = value;
  }

  addSection(kind: ValueAtRiskSectionKind) {
    this.input.sections.push(emptySection(kind));
  }

  removeSection(sectionIndex: number) {
    this.input.sections.splice(sectionIndex, 1);
  }

  setSectionField(sectionIndex: number, field: SectionField, value: string) {
    this.input.sections[sectionIndex][field] = value;
  }

  setUnlimited(sectionIndex: number, unlimited: boolean) {
    this.input.sections[sectionIndex].sumInsuredUnlimited = unlimited;
  }

  addRow(sectionIndex: number) {
    this.input.sections[sectionIndex].rows.push(emptyRow());
  }

  removeRow(sectionIndex: number, rowIndex: number) {
    this.input.sections[sectionIndex].rows.splice(rowIndex, 1);
  }

  setRowField(sectionIndex: number, rowIndex: number, field: RowField, value: string) {
    this.input.sections[sectionIndex].rows[rowIndex][field] = value;
  }
}
